import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import questionScreenImg from '@/components/Image_resources/Questions.jpg';
import arenaScreenImg from '@/components/Image_resources/Arena.png';
import bonusScreenImg from '@/components/Image_resources/Bonus_round.png';

interface HowToPlayModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type HowToPlayImageCard = {
  src: string;
  alt: string;
  title: string;
  caption: string;
  detail?: string;
  extraContent?: ReactNode;
};

const HowToPlayModal = ({ isOpen, onClose }: HowToPlayModalProps) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const imageCards: HowToPlayImageCard[] = [
    {
      src: questionScreenImg,
      alt: 'Question screen showing a multiple-choice prompt',
      title: 'Question Screen',
      caption:
        'Read the question, find the correct answer. You do not choose your answer on this page yet!'
    },
    {
      src: arenaScreenImg,
      alt: 'Game arena with characters lined up on answer circles',
      title: 'Answer Arena',
      caption:
        'Use the arrow keys to guide your creature onto the answer circle that matches your pick and stand on it for 0.5 second to lock it in.',
      extraContent: <ArrowKeyDemo />
    },
    {
      src: bonusScreenImg,
      alt: 'Bonus round card showing Capture Net power-up',
      title: 'Bonus Rounds',
      caption: 'Bonus rounds drop power-ups like Capture Net that you can use during the answer arena. They provide an explaination on how to use them too.'
    }
  ];

  const modalContent = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="how-to-play-title"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(18, 32, 68, 0.85)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '1.5rem',
        boxSizing: 'border-box',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'linear-gradient(160deg, rgba(255,255,255,0.98) 0%, rgba(240,245,255,0.95) 100%)',
          borderRadius: '24px',
          width: 'min(720px, 95vw)',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 30px 80px rgba(9, 14, 34, 0.45)',
          border: '4px solid rgba(102, 126, 234, 0.25)',
          position: 'relative',
          padding: '2.5rem'
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close how to play guide"
          style={{
            position: 'absolute',
            top: '1.5rem',
            right: '1.5rem',
            background: 'rgba(102, 126, 234, 0.15)',
            border: 'none',
            borderRadius: '999px',
            padding: '0.5rem 1rem',
            color: '#2F327D',
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 6px 16px rgba(102, 126, 234, 0.25)'
          }}
        >
          ✖
        </button>

        <h2
          id="how-to-play-title"
          style={{
            marginBottom: '1.5rem',
            fontSize: '2.2rem',
            color: '#2F327D',
            textAlign: 'center',
            letterSpacing: '0.02em'
          }}
        >
          🧭 How To Play
        </h2>

        <p
          style={{
            marginBottom: '2rem',
            textAlign: 'center',
            color: '#4C4F7A',
            fontSize: '1.1rem',
            lineHeight: 1.6
          }}
        >
          Follow this quick guide so you can jump in the moment your teacher starts the game.
        </p>

        <div
          style={{
            margin: '2.5rem 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '2.5rem',
            alignItems: 'center'
          }}
        >
          {imageCards.map((imageCard) => (
            <div
              key={imageCard.title}
              style={{
                background: '#ffffff',
                borderRadius: '24px',
                padding: '1.75rem',
                boxShadow: '0 28px 60px rgba(47, 50, 125, 0.14)',
                border: '1px solid rgba(102, 126, 234, 0.15)',
                width: 'min(720px, 100%)',
                display: 'flex',
                flexDirection: 'column',
                gap: '1.25rem'
              }}
            >
              <img
                src={imageCard.src}
                alt={imageCard.alt}
                style={{
                  width: '100%',
                  borderRadius: '20px',
                  objectFit: 'cover',
                  boxShadow: '0 22px 45px rgba(9, 14, 34, 0.18)',
                  maxHeight: '420px'
                }}
              />
              <div>
                <h3 style={{ marginBottom: '0.5rem', color: '#2F327D', fontSize: '1.35rem', letterSpacing: '0.01em' }}>
                  {imageCard.title}
                </h3>
                <p style={{ color: '#4C4F7A', lineHeight: 1.7, fontSize: '1.05rem' }}>{imageCard.caption}</p>
                {imageCard.detail && (
                  <p style={{ color: '#3D4178', lineHeight: 1.7, marginTop: '0.75rem', fontSize: '1.02rem' }}>
                    {imageCard.detail}
                  </p>
                )}
              </div>
              {imageCard.extraContent}
            </div>
          ))}
        </div>

        <div
          style={{
            display: 'grid',
            gap: '1.5rem',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))'
          }}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(56, 142, 60, 0.15) 100%)',
              borderRadius: '18px',
              padding: '1.75rem',
              border: '1px solid rgba(76, 175, 80, 0.4)'
            }}
          >
            <h3 style={{ marginBottom: '0.75rem', color: '#2E7D32', fontSize: '1.25rem' }}>
              ⚡ Finish Fast For Bonus Points
            </h3>
            <p style={{ color: '#345234', lineHeight: 1.7 }}>
              The faster you lock onto the correct answer tile compared with the group average, the more bonus points you stack.
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: '2.5rem',
            textAlign: 'center',
            color: '#2F327D',
            fontWeight: 700,
            fontSize: '1.2rem'
          }}
        >
          You are ready to roll the moment your teacher hits start. See you in the arena! 🚀
        </div>

        <div
          style={{
            marginTop: '2rem',
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '0.95rem 2.8rem',
              borderRadius: '999px',
              border: 'none',
              background: 'linear-gradient(135deg, #5169E3 0%, #344CC8 100%)',
              color: '#FFFFFF',
              fontSize: '1rem',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 14px 30px rgba(52, 76, 200, 0.3)'
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

type ArrowKeyState = {
  ArrowUp: boolean;
  ArrowDown: boolean;
  ArrowLeft: boolean;
  ArrowRight: boolean;
};

const createArrowKeyState = (): ArrowKeyState => ({
  ArrowUp: false,
  ArrowDown: false,
  ArrowLeft: false,
  ArrowRight: false
});

type ArrowKeyLayout = {
  code: keyof ArrowKeyState;
  label: string;
  area: 'up' | 'down' | 'left' | 'right';
};

const arrowKeyLayout: ArrowKeyLayout[] = [
  { code: 'ArrowUp', label: '↑', area: 'up' },
  { code: 'ArrowLeft', label: '←', area: 'left' },
  { code: 'ArrowDown', label: '↓', area: 'down' },
  { code: 'ArrowRight', label: '→', area: 'right' }
];

const arrowKeyGridPosition: Record<keyof ArrowKeyState, { column: number; row: number }> = {
  ArrowUp: { column: 2, row: 1 },
  ArrowLeft: { column: 1, row: 2 },
  ArrowDown: { column: 2, row: 2 },
  ArrowRight: { column: 3, row: 2 }
};

const isArrowKeyCode = (key: string): key is keyof ArrowKeyState =>
  key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight';

const ArrowKeyDemo = () => {
  const [pressedKeys, setPressedKeys] = useState<ArrowKeyState>(() => createArrowKeyState());

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isArrowKeyCode(event.key)) {
        return;
      }

      const key = event.key as keyof ArrowKeyState;
      event.preventDefault();
      setPressedKeys((prev) => {
        if (prev[key]) {
          return prev;
        }

        return { ...prev, [key]: true };
      });
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (!isArrowKeyCode(event.key)) {
        return;
      }

      const key = event.key as keyof ArrowKeyState;
      setPressedKeys((prev) => {
        if (!prev[key]) {
          return prev;
        }

        return { ...prev, [key]: false };
      });
    };

    const handleBlur = () => {
      setPressedKeys(() => createArrowKeyState());
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  return (
    <div
      style={{
        marginTop: '0.5rem',
        padding: '1.75rem',
        borderRadius: '20px',
        background: 'linear-gradient(150deg, rgba(102, 126, 234, 0.24) 0%, rgba(118, 75, 162, 0.18) 100%)',
        border: '1px solid rgba(102, 126, 234, 0.28)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1.25rem'
      }}
    >
      <p style={{ margin: 0, color: '#2F327D', fontWeight: 700, letterSpacing: '0.01em' }}>Press your arrow keys to try it</p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 84px)',
          gridTemplateRows: 'repeat(2, 84px)',
          gridTemplateAreas: '". up ." "left down right"',
          gap: '0.6rem',
          justifyItems: 'center',
          alignItems: 'center',
          perspective: '1000px'
        }}
      >
        {arrowKeyLayout.map((arrowKey) => {
          const isActive = pressedKeys[arrowKey.code];
          const position = arrowKeyGridPosition[arrowKey.code];

          const keyVisualStyle: CSSProperties = {
            gridArea: arrowKey.area,
            gridColumn: `${position.column}`,
            gridRow: `${position.row}`,
            width: '84px',
            height: '84px',
            borderRadius: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.65rem',
            fontWeight: 700,
            color: isActive ? '#FFFFFF' : '#2F327D',
            background: isActive
              ? 'linear-gradient(165deg, #7186FF 0%, #384ED4 100%)'
              : 'linear-gradient(165deg, #FFFFFF 0%, #E8EBFF 100%)',
            border: isActive ? '2px solid rgba(255, 255, 255, 0.75)' : '2px solid rgba(47, 50, 125, 0.2)',
            boxShadow: isActive
              ? '0 14px 28px rgba(37, 54, 163, 0.38), inset 0 5px 14px rgba(255, 255, 255, 0.55)'
              : '0 24px 42px rgba(47, 50, 125, 0.18), inset 0 3px 10px rgba(255, 255, 255, 0.5)',
            transform: isActive ? 'translateY(8px)' : 'translateY(-1px)',
            transition:
              'transform 120ms ease, box-shadow 180ms ease, background 160ms ease, border 160ms ease, color 160ms ease',
            textShadow: isActive ? '0 4px 12px rgba(0, 0, 0, 0.25)' : '0 1px 0 rgba(255, 255, 255, 0.45)',
            userSelect: 'none',
            willChange: 'transform, box-shadow'
          };

          return (
            <div key={arrowKey.code} style={keyVisualStyle} aria-hidden="true">
              {arrowKey.label}
            </div>
          );
        })}
      </div>
      <p style={{ margin: 0, color: '#454981', lineHeight: 1.6, textAlign: 'center', fontSize: '0.98rem' }}>
        Each key dips and lights up with your press so you can feel the motion before the match begins.
      </p>
    </div>
  );
};

export default HowToPlayModal;
